/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionIssuersCimdClientsVerifyURL } from "../funcs/userSessionIssuersCimdClientsVerifyURL.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { VerifyCimdURLResult } from "../models/components/verifycimdurlresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  VerifyUserSessionIssuerCimdClientURLRequest,
  VerifyUserSessionIssuerCimdClientURLSecurity,
} from "../models/operations/verifyusersessionissuercimdclienturl.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type VerifyUserSessionIssuerCimdClientURLMutationVariables = {
  request: VerifyUserSessionIssuerCimdClientURLRequest;
  security?: VerifyUserSessionIssuerCimdClientURLSecurity | undefined;
  options?: RequestOptions;
};

export type VerifyUserSessionIssuerCimdClientURLMutationData =
  VerifyCimdURLResult;

export type VerifyUserSessionIssuerCimdClientURLMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * verifyURL userSessionIssuersCimdClients
 *
 * @remarks
 * Check that a CIMD document URL is reachable and spec-compliant, without saving anything. A pre-flight for create: the same fetch and validation the authorization server performs, reported in full so an operator can fix the URL before adding it. Every probe outcome is a 200 with verified true or false — errors are reserved for a malformed request, missing authorization, or an exceeded rate limit. Rate limited per project, since this is the one endpoint that makes Speakeasy fetch a caller-chosen URL.
 */
export function useVerifyUserSessionIssuerCimdClientURLMutation(
  options?: MutationHookOptions<
    VerifyUserSessionIssuerCimdClientURLMutationData,
    VerifyUserSessionIssuerCimdClientURLMutationError,
    VerifyUserSessionIssuerCimdClientURLMutationVariables
  >,
): UseMutationResult<
  VerifyUserSessionIssuerCimdClientURLMutationData,
  VerifyUserSessionIssuerCimdClientURLMutationError,
  VerifyUserSessionIssuerCimdClientURLMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildVerifyUserSessionIssuerCimdClientURLMutation(client, options),
    ...options,
  });
}

export function mutationKeyVerifyUserSessionIssuerCimdClientURL(): MutationKey {
  return ["@gram/client", "userSessionIssuersCimdClients", "verifyURL"];
}

export function buildVerifyUserSessionIssuerCimdClientURLMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: VerifyUserSessionIssuerCimdClientURLMutationVariables,
  ) => Promise<VerifyUserSessionIssuerCimdClientURLMutationData>;
} {
  return {
    mutationKey: mutationKeyVerifyUserSessionIssuerCimdClientURL(),
    mutationFn: function verifyUserSessionIssuerCimdClientURLMutationFn({
      request,
      security,
      options,
    }): Promise<VerifyUserSessionIssuerCimdClientURLMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionIssuersCimdClientsVerifyURL(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
